import { VariantProps, cva } from 'class-variance-authority';
import { Drawer as DrawerPrimitive } from 'vaul';
import * as React from 'react';

import { typographyVariants } from '../typography/typography';
import { cn } from '../../utils';

export const drawerContentVariants = cva(
  'fixed flex flex-col border-border-default overflow-hidden z-50 gap-4 bg-background shadow-lg transition ease-in-out !select-text data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out focus-visible:outline-none',
  {
    variants: {
      direction: {
        top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
        bottom:
          'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
        left: 'inset-y-0 left-0 max-w-125 w-auto h-full border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm md:max-w-md lg:max-w-lg',
        right:
          'inset-y-0 right-0 w-[31.25rem] h-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm md:max-w-md lg:max-w-lg',
      },
    },
    defaultVariants: {
      direction: 'right',
    },
  },
);

export interface DrawerProps extends VariantProps<typeof drawerContentVariants> {
  hasBackdrop?: boolean;
}

const DrawerContext = React.createContext<Pick<DrawerProps, 'hasBackdrop' | 'direction'>>({});

const Drawer = ({
  shouldScaleBackground = true,
  direction = 'right',
  hasBackdrop = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root> & DrawerProps) => {
  return (
    <DrawerContext.Provider value={{ hasBackdrop: hasBackdrop, direction }}>
      <DrawerPrimitive.Root
        shouldScaleBackground={shouldScaleBackground}
        direction={direction}
        modal={hasBackdrop}
        {...props}
      />
    </DrawerContext.Provider>
  );
};
Drawer.displayName = 'Drawer';

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-50 bg-dialog-backdrop/24 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    ref={ref}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  const { hasBackdrop, direction } = React.useContext(DrawerContext);

  const content = (
    <DrawerPrimitive.Content
      className={cn(drawerContentVariants({ direction }), className)}
      aria-describedby={undefined}
      ref={ref}
      {...props}
    >
      {children}
    </DrawerPrimitive.Content>
  );

  return (
    <DrawerPortal>{hasBackdrop ? <DrawerOverlay>{content}</DrawerOverlay> : content}</DrawerPortal>
  );
});
DrawerContent.displayName = 'DrawerContent';

const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('grid p-4 text-center sm:text-left border-b border-border-default', className)}
    {...props}
  />
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('mt-auto flex flex-col gap-2 p-4', className)} {...props} />
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    className={cn(
      'flex items-center justify-between leading-none tracking-tight',
      typographyVariants({ variant: 'h200' }),
      className,
    )}
    ref={ref}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description className={cn('text-sm', className)} ref={ref} {...props} />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  DrawerDescription,
  DrawerOverlay,
  DrawerTrigger,
  DrawerContent,
  DrawerPortal,
  DrawerHeader,
  DrawerFooter,
  DrawerClose,
  DrawerTitle,
  Drawer,
};
